import { useContext, useState } from "react";
import { Box, Stack, Typography, Button } from "@mui/joy";
import { motion, AnimatePresence } from "framer-motion";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import PaletteRoundedIcon from "@mui/icons-material/PaletteRounded";
import ApartmentRoundedIcon from "@mui/icons-material/ApartmentRounded";
import StorefrontRoundedIcon from "@mui/icons-material/StorefrontRounded";
import HomeRoundedIcon from "@mui/icons-material/HomeRounded";

import { Section, Container, SectionHeader, COLORS, BlueprintGrid } from "../shared/ui";
import { LayoutContext } from "../context";

const SEGMENTS = [
  {
    id: "designers",
    icon: (color) => <PaletteRoundedIcon sx={{ color: color }} />,
    title: "Дизайнерам интерьера",
    headline: "Точные исходники — без переделок на стройке",
    points: [
      "Файл в ArchiCAD, Revit или AutoCAD — сразу открывается в вашей программе",
      "Диагонали, отклонения стен и перепады пола — всё в чертеже",
      "Фотоотчёт каждой стены для проверки любой точки удалённо",
      "Работаем с Яндекс.Диском, ваш проектный архив не перегружается",
    ],
  },
  {
    id: "architects",
    icon: (color) => <ApartmentRoundedIcon sx={{ color: color }} />,
    title: "Архитекторам и бюро",
    headline: "Сложная геометрия и инженерка в базовом пакете",
    points: [
      "Замер сводов, лестниц, многоуровневых потолков, колонн",
      "Инженерные коммуникации: электрика, сантехника, вентиляция",
      "3D-сканирование на сложные объекты — по запросу",
      "Документация для технадзора и согласования",
    ],
  },
  {
    id: "retail",
    icon: (color) => <StorefrontRoundedIcon sx={{ color: color }} />,
    title: "Торговым сетям и франшизам",
    headline: "Единый стандарт отчёта в 150+ городах",
    points: [
      "Опыт с Burger King, Rostic's, Живикой, Жизньмартом, Счастливым Взглядом",
      "Замер десятков объектов по единому регламенту сети",
      "Договор с НДС, отсрочка для постоянных клиентов",
      "Личный менеджер проекта и единая точка входа",
    ],
  },
  {
    id: "private",
    icon: (color) => <HomeRoundedIcon sx={{ color: color }} />,
    title: "Частным заказчикам",
    headline: "От 7 000 ₽ за выезд — даже для маленькой квартиры",
    points: [
      "Готовность за 1 день — для срочных задач",
      "Личный кабинет с онлайн-отслеживанием статуса",
      "План для ремонта, перепланировки или продажи квартиры",
      "Все замеры с фото — можно передать подрядчику",
    ],
  },
];

export default function ForWhom({ onShowOrder }) {
  const { setQuickOrderCalcAmount, setShowQuickOrder } = useContext(LayoutContext);
  const [active, setActive] = useState(SEGMENTS[0].id);
  const activeSegment = SEGMENTS.find((s) => s.id === active);

  const handleQuickOrder = () => {
    setQuickOrderCalcAmount(null);
    setShowQuickOrder(true);
  };

  return (
    <Section id="for-whom" py={{ xs: 10, md: 14 }} sx={{ background: COLORS.bgLight }}>
      <BlueprintGrid opacity={0.04} />

      <Container maxWidth={1180}>
        <SectionHeader
          eyebrow="Для кого мы работаем"
          title="Под каждый сегмент — свой подход"
          subtitle="Мы работаем с профессионалами больше 8 лет и знаем, что важно именно для вас"
        />

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: "340px 1fr" },
            gap: { xs: 3, md: 4 },
            alignItems: "start",
          }}
        >
          {/* Табы слева */}
          <Stack spacing={1.25}>
            {SEGMENTS.map((segment) => (
              <TabButton
                key={segment.id}
                segment={segment}
                active={active === segment.id}
                onClick={() => setActive(segment.id)}
              />
            ))}
          </Stack>

          {/* Контент справа */}
          <Box
            sx={{
              position: "relative",
              minHeight: 440,
              borderRadius: "24px",
              background: "#fff",
              border: "1px solid rgba(99,102,241,0.12)",
              boxShadow: "0 20px 60px rgba(99,102,241,0.08)",
              p: { xs: 3, md: 5 },
              overflow: "hidden",
            }}
          >
            {/* Декоративный угол */}
            <Box
              sx={{
                position: "absolute",
                top: 0,
                right: 0,
                width: 200,
                height: 200,
                background: `radial-gradient(circle at top right, rgba(99,102,241,0.08), transparent 70%)`,
                pointerEvents: "none",
              }}
            />

            <AnimatePresence mode="wait">
              <motion.div
                key={active}
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -12 }}
                transition={{ duration: 0.3, ease: [0.4, 0, 0.2, 1] }}
                style={{ position: "relative" }}
              >
                {/* Крупная иконка */}
                <Box
                  sx={{
                    width: 64,
                    height: 64,
                    borderRadius: "16px",
                    background: `linear-gradient(135deg, ${COLORS.brand}, ${COLORS.brand2})`,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: "#fff",
                    mb: 3,
                    boxShadow: "0 12px 32px rgba(99,102,241,0.35)",
                    "& svg": { fontSize: 32 },
                  }}
                >
                  {activeSegment.icon(active ? 'white' : 'rgba(99, 101, 241, 0.64)')}
                </Box>

                <Typography
                  level="body-xs"
                  sx={{
                    color: COLORS.brand,
                    fontWeight: 700,
                    textTransform: "uppercase",
                    letterSpacing: "0.14em",
                    mb: 1,
                  }}
                >
                  {activeSegment.title}
                </Typography>
                <Typography
                  level="h3"
                  sx={{
                    fontWeight: 800,
                    color: COLORS.dark,
                    fontSize: { xs: "1.5rem", md: "1.9rem" },
                    letterSpacing: "-0.8px",
                    lineHeight: 1.2,
                    mb: 3,
                  }}
                >
                  {activeSegment.headline}
                </Typography>

                <Stack spacing={1.5} sx={{ mb: 4 }}>
                  {activeSegment.points.map((point, i) => (
                    <motion.div
                      key={point}
                      initial={{ opacity: 0, x: -12 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ duration: 0.35, delay: i * 0.06 }}
                    >
                      <Stack direction="row" spacing={1.5} alignItems="flex-start">
                        <CheckCircleRoundedIcon
                          sx={{ color: COLORS.brand, fontSize: 22, mt: 0.1, flexShrink: 0 }}
                        />
                        <Typography level="body-md" sx={{ color: "neutral.700", lineHeight: 1.55 }}>
                          {point}
                        </Typography>
                      </Stack>
                    </motion.div>
                  ))}
                </Stack>

                <motion.div whileHover={{ scale: 1.03 }} whileTap={{ scale: 0.97 }} style={{ display: "inline-block" }}>
                  <Button
                    size="lg"
                    onClick={onShowOrder}
                    onClick={handleQuickOrder}
                    sx={{
                      fontWeight: 700,
                      px: 4,
                      borderRadius: "12px",
                      background: `linear-gradient(135deg, ${COLORS.brand}, ${COLORS.brand2})`,
                      boxShadow: "0 8px 24px rgba(99,102,241,0.35)",
                      "&:hover": {
                        background: `linear-gradient(135deg, ${COLORS.brand}, ${COLORS.brand2})`,
                        boxShadow: "0 12px 32px rgba(99,102,241,0.5)",
                      },
                    }}
                  >
                    Оставить заявку
                  </Button>
                </motion.div>
              </motion.div>
            </AnimatePresence>
          </Box>
        </Box>
      </Container>
    </Section>
  );
}

function TabButton({ segment, active, onClick }) {
  return (
    <Box
      onClick={onClick}
      sx={{
        cursor: "pointer",
        p: 2.25,
        borderRadius: "14px",
        border: `2px solid ${active ? COLORS.brand : "rgba(99,102,241,0.12)"}`,
        background: active ? "#fff" : "rgba(255,255,255,0.5)",
        transition: "all 0.25s ease",
        position: "relative",
        "&:hover": {
          borderColor: active ? COLORS.brand : "rgba(99,102,241,0.3)",
          background: "#fff",
        },
      }}
    >
      <Stack direction="row" spacing={2} alignItems="center">
        <Box
          sx={{
            width: 44,
            height: 44,
            flexShrink: 0,
            borderRadius: "12px",
            background: active
              ? `linear-gradient(135deg, ${COLORS.brand}, ${COLORS.brand2})`
              : "rgba(99,102,241,0.08)",
            color: active ? "#fff" : COLORS.brand,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            transition: "all 0.25s ease",
            "& svg": { fontSize: 24 },
          }}
        >
          {segment.icon(active ? 'white' : 'rgba(99, 101, 241, 0.64)')}
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            level="title-sm"
            sx={{
              fontWeight: 700,
              color: active ? COLORS.dark : "neutral.700",
              lineHeight: 1.3,
            }}
          >
            {segment.title}
          </Typography>
        </Box>
        {active && (
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ duration: 0.2 }}
          >
            <Box
              sx={{
                width: 8,
                height: 8,
                borderRadius: "50%",
                background: COLORS.brand,
                boxShadow: `0 0 12px ${COLORS.brand}`,
              }}
            />
          </motion.div>
        )}
      </Stack>
    </Box>
  );
}